/**
 * Cotizaciones en el Portal de Clientes.
 *
 * Todo pasa por RPC que deciden con la identidad autenticada (auth.uid()):
 * el navegador nunca envía user_id, correo, RUT ni rol, y nunca lee la tabla
 * cotizaciones ni recibe tokens. Los importes los calcula la base con la misma
 * regla del panel; aquí solo se muestran.
 */
import { supabase } from "./supabase";

// Aviso al equipo: mismo webhook y formato que usa el panel al responder una cotización.
// El fallo de este aviso nunca revierte la decisión (ya quedó en la base).
export const N8N_COTIZACION_RESPONDIDA = "https://n8n.tuaplicacion.cl/webhook/cotizacion-respondida";
// Pendiente de crear en n8n: sin este flujo la solicitud igual queda en la base y visible en el panel.
export const N8N_COTIZACION_CAMBIOS = "https://n8n.tuaplicacion.cl/webhook/cotizacion-cambios-solicitados";

export const ESTADOS_COTIZACION = {
  Pendiente: { label: "Pendiente de revisión", color: "#f59e0b", bg: "#fef3c7" },
  Aprobada:  { label: "Aprobada",              color: "#10b981", bg: "#d1fae5" },
  Rechazada: { label: "Rechazada",             color: "#ef4444", bg: "#fee2e2" },
  Expirada:  { label: "Expirada",              color: "#f97316", bg: "#ffedd5" },
};

export const FILTROS_COTIZACION = [
  { id: "Pendiente", label: "Pendientes" },
  { id: "Aprobada",  label: "Aprobadas" },
  { id: "Rechazada", label: "Rechazadas" },
  { id: "Expirada",  label: "Expiradas" },
  { id: "todas",     label: "Todas" },
];

export const filtrarCotizaciones = (lista, filtro) =>
  filtro === "todas" ? lista : lista.filter(c => c.estado === filtro);

export const ACCIONES_RESPUESTA = {
  aprobada:  { label: "Aprobada",            color: "#10b981" },
  rechazada: { label: "Rechazada",           color: "#ef4444" },
  cambios:   { label: "Cambios solicitados", color: "#6366f1" },
};

export const fmtCLP = (n) => `$${Number(n || 0).toLocaleString("es-CL")}`;

// Mensajes claros para errores de red / sesión / permisos.
export function mensajeErrorCotizacion(e) {
  const msg = String(e?.message || "");
  const codigo = String(e?.code || "");
  if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) return "No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.";
  if (codigo === "42501" || /No autorizado|iniciar sesión/i.test(msg)) return "No tienes acceso a esta cotización o tu sesión expiró.";
  if (["PGRST202", "PGRST205", "42883", "42P01"].includes(codigo)) return "Las cotizaciones del portal aún no están disponibles. Intenta más tarde.";
  return msg || "No fue posible completar la operación. Intenta de nuevo.";
}

export async function listarMisCotizaciones() {
  const { data, error } = await supabase.rpc("portal_mis_cotizaciones");
  if (error) throw error;
  return data || [];
}

export async function detalleCotizacion(id) {
  const { data, error } = await supabase.rpc("portal_cotizacion_detalle", { p_id: id });
  if (error) throw error;
  return data;
}

// accion: 'aprobar' | 'rechazar' | 'cambios'. hash = contenido_hash que vio el cliente.
export async function responderCotizacion({ id, accion, comentario = "", hash = null }) {
  const { data, error } = await supabase.rpc("portal_cotizacion_responder",
    { p_id: id, p_accion: accion, p_comentario: comentario, p_hash: hash });
  if (error) throw error;
  if (!data?.success) {
    const e = new Error(data?.error || "No se pudo registrar tu respuesta.");
    e.codigo = data?.codigo;
    throw e;
  }
  return data;
}

// Mejor esfuerzo, sin bloquear ni revertir nada si falla.
export async function notificarRespuesta(r, { firmadoPor = "" } = {}) {
  const cambios = r.accion === "cambios";
  try {
    await fetch(cambios ? N8N_COTIZACION_CAMBIOS : N8N_COTIZACION_RESPONDIDA, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        cotizacion_id: r.id, cliente: r.cliente, email_cliente: r.email_cliente,
        estado: cambios ? "Cambios solicitados" : r.estado, comentario: r.comentario,
        firmado_por: firmadoPor, total_clp: r.total, version: r.version, origen: "portal",
      }),
    });
    return true;
  } catch (e) {
    console.warn("n8n webhook no disponible:", e);
    return false;
  }
}

// ── Inicio: indicadores y novedades a partir de lo que ya entregan las RPC ──
export function resumenInicio(proyectos = [], cotizaciones = []) {
  return {
    proyectos: proyectos.length,
    enEjecucion: proyectos.filter(p => p.estado === "Activo").length,
    pendientes: cotizaciones.filter(c => c.estado === "Pendiente" && c.puede_responder).length,
    aprobadas: cotizaciones.filter(c => c.estado === "Aprobada").length,
  };
}

export function novedadesInicio(proyectos = [], cotizaciones = [], max = 6) {
  const items = [];
  proyectos.forEach(p => {
    if (Number(p.comentarios_nuevos) > 0) {
      items.push({ tipo: "comentarios", id: p.id, fecha: p.ultimo_comentario_at || p.created_at,
        texto: `${p.comentarios_nuevos} comentario${Number(p.comentarios_nuevos) !== 1 ? "s" : ""} nuevo${Number(p.comentarios_nuevos) !== 1 ? "s" : ""} en “${p.nombre}”` });
    }
  });
  cotizaciones.forEach(c => {
    if (c.puede_responder) {
      items.push({ tipo: "cotizacion_pendiente", id: c.id, fecha: c.fecha,
        texto: `La cotización ${c.id} espera tu revisión (${fmtCLP(c.total)})` });
    } else if (Number(c.solicitudes_abiertas) > 0) {
      items.push({ tipo: "cotizacion_cambios", id: c.id, fecha: c.fecha,
        texto: `Tu solicitud de cambios en ${c.id} está en revisión` });
    }
  });
  return items.sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || ""))).slice(0, max);
}

// ── Administración (solo es_admin() en la base) ─────────────────────────────
async function rpcAdmin(nombre, args) {
  const { data, error } = await supabase.rpc(nombre, args);
  if (error) throw error;
  if (data && data.success === false) throw new Error(data.error || "No fue posible completar la operación.");
  return data;
}
export const portalAdminCotizacion = (id) => rpcAdmin("cotizacion_portal_admin", { p_id: id });
export const asignarCotizacion = (id, userId, contactoId = null) =>
  rpcAdmin("cotizacion_asignar", { p_cotizacion_id: id, p_user_id: userId, p_contacto_id: contactoId });
export const revocarAccesoCotizacion = (accesoId, motivo = "") =>
  rpcAdmin("cotizacion_revocar_acceso", { p_acceso_id: accesoId, p_motivo: motivo });
export const marcarSolicitudAtendida = (respuestaId) =>
  rpcAdmin("cotizacion_marcar_atendida", { p_respuesta_id: respuestaId });
export const nuevaVersionCotizacion = (id, motivo = "") =>
  rpcAdmin("cotizacion_nueva_version", { p_id: id, p_motivo: motivo });

// El trigger de la base rechaza editar una cotización aprobada: mensaje para el panel.
export const esErrorCotizacionAprobada = (e) => /COTIZACION_APROBADA/.test(String(e?.message || ""));
